import { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { ROLES, RoleType } from '../types';

interface RoleConfig {
  roleId: RoleType;
  count: number;
}

interface RoleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerCount: number;
  roleConfigs: RoleConfig[];
  onSave: (configs: RoleConfig[]) => void;
}

export default function RoleConfigModal({
  isOpen,
  onClose,
  playerCount,
  roleConfigs,
  onSave,
}: RoleConfigModalProps) {
  const [localConfigs, setLocalConfigs] = useState<RoleConfig[]>(roleConfigs);

  useEffect(() => {
    setLocalConfigs(roleConfigs);
  }, [roleConfigs]);

  if (!isOpen) return null;

  const updateRoleCount = (roleId: RoleType, delta: number) => {
    const newConfigs = localConfigs.map((config) => {
      if (config.roleId === roleId) {
        const newCount = Math.max(0, config.count + delta);
        return { ...config, count: newCount };
      }
      return config;
    });
    setLocalConfigs(newConfigs);
  };

  const getTotalRoles = () => {
    return localConfigs.reduce((sum, config) => sum + config.count, 0);
  };

  const handleSave = () => {
    onSave(localConfigs);
    onClose();
  };

  const availableGoodRoles: RoleType[] = ['merlin', 'percival', 'loyal'];
  const availableEvilRoles: RoleType[] = ['mordred', 'morgana', 'assassin', 'oberon'];

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-avalon-card rounded-xl p-5 w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">自定义角色配置</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-blue-400 text-sm font-medium">正义阵营</span>
          </div>
          <div className="space-y-2">
            {availableGoodRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-blue-500/20'
                      : 'bg-blue-500/20 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
          </div>
          <div className="space-y-2">
            {availableEvilRoles.map((roleId) => {
              const config = localConfigs.find(c => c.roleId === roleId);
              if (!config) return null;
              const role = ROLES[roleId];
              const isZeroCount = config.count === 0;
              return (
                <div
                  key={roleId}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                    isZeroCount
                      ? 'bg-transparent border-red-500/20'
                      : 'bg-red-500/20 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{role.icon}</span>
                    <div className="text-white text-sm font-medium">
                      {role.name} <span className="text-avalon-gold ml-1">×{config.count}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateRoleCount(roleId, -1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white font-medium w-5 text-center text-sm">{config.count}</span>
                    <button
                      onClick={() => updateRoleCount(roleId, 1)}
                      className="w-6 h-6 rounded-lg bg-avalon-purple text-white flex items-center justify-center hover:bg-avalon-purple/70 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-avalon-bg-dark/30 mb-4">
          <span className="text-avalon-text text-sm">当前配置</span>
          <span className={`text-sm font-medium ${
            getTotalRoles() === playerCount ? 'text-green-400' : 'text-avalon-gold'
          }`}>
            {getTotalRoles()} / {playerCount} 人
          </span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-avalon-bg-dark text-white font-medium hover:bg-avalon-purple/50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={getTotalRoles() !== playerCount}
            className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${
              getTotalRoles() === playerCount
                ? 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                : 'bg-avalon-bg-dark/50 text-avalon-text cursor-not-allowed'
            }`}
          >
            保存配置
          </button>
        </div>
      </div>
    </div>
  );
}
